// src/pages/guides/EASMComplianceGuide.js
// EASM for Compliance Guide (G7) - Maps EASM capabilities to PCI DSS 4.0, NIS2, SOC 2

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function EASMComplianceGuide() {
  const startTrial = useTrialCta('guide_trial:easm-compliance');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "EASM for Compliance: PCI DSS, NIS2, and SOC 2",
    "description": "Map EASM capabilities to PCI DSS 4.0, NIS2, and SOC 2 controls. Learn how to use external attack surface data as audit evidence.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-08-26T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/easm-compliance",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/easm-compliance",
    "keywords": ["EASM compliance", "attack surface management PCI DSS", "EASM NIS2", "external attack surface compliance", "security compliance monitoring"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "EASM for Compliance" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="EASM for Compliance: PCI DSS, NIS2, SOC 2 | DriftAlarm"
        description="Map EASM capabilities to PCI DSS 4.0, NIS2, and SOC 2 controls. Learn how to use external attack surface data as audit evidence."
        path="/guides/easm-compliance"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/easm-compliance')].filter(Boolean)}
      />

      {/* Navigation Bar */}
      <PublicHeader activeLink="guides" />

      {/* Main Content */}
      <main className="guide-content">
        {/* Hero Section */}
        <section className="guide-hero">
          <h1>EASM for Compliance: PCI DSS, NIS2, and SOC 2</h1>
          <GuideByline path="/guides/easm-compliance" />
          <GuideAnswer path="/guides/easm-compliance" />
          <p className="guide-hero-intro">
            Auditors are no longer satisfied with annual penetration tests. Compliance frameworks now
            demand continuous visibility into your external attack surface. This guide maps EASM
            capabilities directly to the controls that auditors check, so you can turn scan data into
            audit evidence.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why auditors now ask about external attack surface visibility</li>
              <li>Specific PCI DSS 4.0, NIS2, and SOC 2 controls that EASM supports</li>
              <li>How to map EASM capabilities to framework requirements</li>
              <li>What to export and present during compliance audits</li>
              <li>A 4-step plan to prepare compliance evidence today</li>
            </ul>
          </div>

          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted, #808080)', marginBottom: '20px' }}>14 min read</p>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Read the Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#why-auditors-ask">Why Auditors Ask About Your Attack Surface</a></li>
            <li><a href="#frameworks">Which Frameworks Require Continuous ASM</a></li>
            <li><a href="#pci-dss">PCI DSS 4.0 Requirements</a></li>
            <li><a href="#nis2">NIS2 Directive Requirements</a></li>
            <li><a href="#soc2">SOC 2 Criteria</a></li>
            <li><a href="#mapping-table">Compliance Mapping Table</a></li>
            <li><a href="#audit-evidence">Using EASM Data as Audit Evidence</a></li>
            <li><a href="#action-steps">What to Do Now: 4 Steps</a></li>
            <li><a href="#driftalarm-compliance">How DriftAlarm Supports Compliance Workflows</a></li>
            <li><a href="#related-guides">Related Security Guides</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section: Why Auditors Ask */}
        <section id="why-auditors-ask" className="guide-section">
          <h2>Why Auditors Are Asking About Your External Attack Surface</h2>
          <p>
            The compliance landscape has shifted. Five years ago, auditors checked whether you had a firewall
            and ran an annual vulnerability scan. Today they want to know whether you have <strong>continuous
            visibility</strong> into every internet-facing asset your organization exposes, and whether you can
            demonstrate that visibility with data.
          </p>
          <p>
            Three forces are driving this change:
          </p>
          <ul>
            <li>
              <strong>Breach patterns have changed.</strong> Unmonitored and forgotten external assets are a
              recurring theme in incident reporting, and auditors have noticed. They now ask specifically
              about unknown asset discovery and configuration drift.
            </li>
            <li>
              <strong>Regulatory frameworks have updated.</strong> PCI DSS 4.0 introduced new requirements
              for external vulnerability management that took full effect in March 2025. NIS2 enforcement
              had an October 2024 transposition deadline across EU member states. Both explicitly reference continuous risk
              assessment and asset inventory obligations.
            </li>
            <li>
              <strong>Point-in-time evidence is no longer sufficient.</strong> An annual penetration test
              report shows your posture on a single day. Auditors increasingly expect evidence of
              ongoing monitoring: trend data, change detection logs, and scheduled scan histories that
              cover the entire audit period.
            </li>
          </ul>
          <p>
            External Attack Surface Management (EASM) directly addresses these expectations. It provides
            the continuous discovery, vulnerability assessment, and change tracking data that auditors
            want to see, organized in a format they can review during control testing.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">Important Disclaimer</div>
            <div className="callout-content">
              <p>
                EASM tools support compliance workflows and help generate evidence for auditors. They
                do <strong>not</strong> guarantee compliance with any framework. Compliance is determined
                by auditors based on your complete control environment, organizational policies, and
                implementation maturity. Always consult your compliance team or qualified assessor for
                authoritative guidance.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Frameworks Overview */}
        <section id="frameworks" className="guide-section">
          <h2>Which Compliance Frameworks Require Continuous ASM</h2>
          <p>
            Three major frameworks are driving the most demand for EASM evidence. Each approaches
            external attack surface visibility from a different angle, but all converge on the
            same principle: you must know what you expose and monitor it continuously.
          </p>

          {/* PCI DSS 4.0 */}
          <h3 id="pci-dss">PCI DSS v4.0.1: External Scanning and Asset Inventory</h3>
          <p>
            PCI DSS v4.0.1 is the current version; v4.0's future-dated requirements became effective in
            March 2025. It places explicit emphasis on maintaining an accurate inventory of system components and performing
            regular external vulnerability scans. Three requirements are particularly relevant to EASM:
          </p>
          <ul>
            <li>
              <strong>Requirement 6.3</strong> (Security vulnerabilities are identified and addressed):
              Organizations must identify and manage security vulnerabilities through a formal process.
              This includes monitoring vulnerability advisories and correlating them against your
              asset inventory. EASM provides the external asset catalog and vulnerability scan results
              that feed this process.
            </li>
            <li>
              <strong>Requirement 12.5.1</strong> (An inventory of system components in scope for PCI DSS
              is maintained and kept current): this is the control that asks for a complete, current asset
              inventory. PCI DSS expects organizations to maintain a current inventory of authorized and
              unauthorized access points. EASM discovery scans identify externally accessible services
              and ports that may constitute unintended access points.
            </li>
            <li>
              <strong>Requirement 11.3</strong> (External and internal vulnerabilities are regularly identified,
              prioritized, and addressed): This is the most directly applicable requirement. It mandates
              external vulnerability scanning at least quarterly by an Approved Scanning Vendor (ASV),
              plus scans after significant changes. EASM provides the continuous scanning layer between
              ASV engagements, helping demonstrate ongoing diligence and catch new exposures between
              quarterly cycles.
            </li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">EASM Complements, Not Replaces, ASV Scans</div>
            <div className="callout-content">
              <p>
                PCI DSS still requires quarterly ASV scans from an approved vendor. EASM does not
                replace this obligation. However, continuous EASM scanning between ASV engagements
                helps demonstrate ongoing vulnerability management and catches new exposures before
                the next quarterly assessment.
              </p>
            </div>
          </div>

          {/* NIS2 */}
          <h3 id="nis2">NIS2 Directive: Risk Management, Incident Handling, Supply Chain</h3>
          <p>
            The NIS2 Directive (Network and Information Security Directive 2) had a transposition deadline of
            17 October 2024, though national implementation has landed at different times across member states. It significantly expands the scope of organizations
            subject to cybersecurity obligations and introduces specific requirements for risk management
            measures. Article 21 is the core requirement:
          </p>
          <ul>
            <li>
              <strong>Risk management measures (Article 21.2a):</strong> Organizations must implement
              risk analysis and information system security policies. This requires knowing what systems
              are exposed and what risks they carry. EASM provides the external risk assessment data
              that feeds these policies: discovered assets, open ports, detected vulnerabilities, and
              risk scores.
            </li>
            <li>
              <strong>Incident handling (Article 21.2b):</strong> NIS2 requires incident prevention,
              detection, and response capabilities. Drift detection and change monitoring from EASM
              tools help identify unauthorized changes to the external surface that may indicate incidents
              or create incident risk. Port monitoring and certificate tracking provide early warning signals.
            </li>
            <li>
              <strong>Supply chain security (Article 21.2d):</strong> Organizations must assess and manage
              security risks from their supply chain and service providers. EASM can discover third-party
              services, hosted assets, and vendor-managed infrastructure that form part of your external
              surface, providing visibility into supply chain exposure.
            </li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">NIS2 Scope Is Broad</div>
            <div className="callout-content">
              <p>
                Some entity types are in scope regardless of size. NIS2 applies to "essential" and
                "important" entities across 18 sectors, including
                energy, transport, healthcare, digital infrastructure, ICT service management, and
                manufacturing. If your organization operates in the EU and employs more than 50 people
                or has annual revenue above 10 million euros, NIS2 likely applies to you.
              </p>
            </div>
          </div>

          {/* SOC 2 */}
          <h3 id="soc2">SOC 2: Logical Access and System Monitoring</h3>
          <p>
            SOC 2 (System and Organization Controls 2) is widely adopted by SaaS vendors and service
            providers. Two Common Criteria are directly supported by EASM data:
          </p>
          <ul>
            <li>
              <strong>CC6.1 (Logical and physical access controls):</strong> This criterion requires
              organizations to implement logical access security measures over information assets. EASM
              helps demonstrate CC6.1 compliance by providing evidence of port monitoring (identifying
              unexpected open ports), service enumeration (detecting unauthorized services), and
              continuous inventory of external access points. Drift detection highlights when new access
              vectors appear without authorization.
            </li>
            <li>
              <strong>CC7.1 (System monitoring):</strong> This criterion requires monitoring of system
              components to detect anomalies and evaluate events. EASM provides continuous monitoring
              evidence: scheduled scan results, drift event logs, vulnerability trend data, and alert
              histories. This data demonstrates that your organization actively monitors its external
              surface for security-relevant changes.
            </li>
          </ul>
          <p>
            SOC 2 audits evaluate controls over a period (typically 6-12 months for Type II). EASM tools
            that maintain historical data across the audit period provide particularly strong evidence,
            because they show consistent monitoring rather than a single point-in-time snapshot.
          </p>
        </section>

        {/* Section: Compliance Mapping Table */}
        <section id="mapping-table" className="guide-section">
          <h2>Compliance Mapping: EASM Capabilities to Framework Controls</h2>
          <p>
            The following table maps specific EASM capabilities to the framework controls they help
            address. Use this as a reference when preparing audit evidence or assessing which
            capabilities your EASM program needs to cover.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>EASM Capability</th>
                  <th>PCI DSS 4.0</th>
                  <th>NIS2</th>
                  <th>SOC 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Asset Discovery</strong></td>
                  <td>Req 12.5.1 — Maintain a current inventory of in-scope system components</td>
                  <td>Art 21.2a — Risk analysis requires complete asset visibility</td>
                  <td>CC6.1 — Inventory of logical access points to information assets</td>
                </tr>
                <tr>
                  <td><strong>Vulnerability Scanning</strong></td>
                  <td>Req 11.3 — External vulnerability scans; Req 6.3 — Identify and manage vulnerabilities</td>
                  <td>Art 21.2a — Technical risk assessment and mitigation</td>
                  <td>CC7.1 — Detect anomalies and evaluate security events</td>
                </tr>
                <tr>
                  <td><strong>Port Monitoring</strong></td>
                  <td>Req 12.5.1 — Keep the component inventory current; Req 11.3 — Identify exposure changes</td>
                  <td>Art 21.2b — Incident prevention through change detection</td>
                  <td>CC6.1 — Monitor and restrict logical access controls</td>
                </tr>
                <tr>
                  <td><strong>Certificate Monitoring</strong></td>
                  <td>Req 4.2 — Protect cardholder data with strong cryptography in transit</td>
                  <td>Art 21.2a — Cryptography and encryption policy measures</td>
                  <td>CC6.1 — Encryption controls over data in transit</td>
                </tr>
                <tr>
                  <td><strong>Drift Detection</strong></td>
                  <td>Req 11.3 — Scan after significant changes; Req 6.5 — Change control processes</td>
                  <td>Art 21.2b — Incident detection through unauthorized change identification</td>
                  <td>CC7.1 — Detect anomalous activity; CC6.1 — Unauthorized access detection</td>
                </tr>
                <tr>
                  <td><strong>Risk Scoring</strong></td>
                  <td>Req 6.3 — Prioritize vulnerabilities by risk ranking</td>
                  <td>Art 21.2a — Risk-based approach to security measures</td>
                  <td>CC7.1 — Evaluate severity of detected events</td>
                </tr>
                <tr>
                  <td><strong>Scheduled Reporting</strong></td>
                  <td>Req 11.3 — Evidence of regular scan execution and remediation tracking</td>
                  <td>Art 21.2f — Policies to assess the effectiveness of risk-management measures</td>
                  <td>CC7.1 — Evidence of continuous monitoring over the audit period</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">Mapping Is Not Certification</div>
            <div className="callout-content">
              <p>
                This mapping shows how EASM capabilities <strong>support</strong> specific controls. It does
                not mean that deploying an EASM tool automatically satisfies these requirements. Each
                framework has additional procedural, organizational, and documentation requirements beyond
                technical tooling. Your qualified assessor or auditor determines whether controls are
                adequately implemented.
              </p>
            </div>
          </div>

          {/* Mid-article CTA */}
          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Building Your Compliance Evidence</span>
              <span>&rarr;</span>
            </button>
            <Link to="/upgrade" className="da-btn da-btn--secondary">
              Continuous compliance monitoring from $99/mo
            </Link>
          </div>
        </section>

        {/* Section: Audit Evidence */}
        <section id="audit-evidence" className="guide-section">
          <h2>Using EASM Data as Audit Evidence</h2>
          <p>
            Raw scan results are not audit evidence. Auditors need data that is organized, contextualized,
            and directly tied to the controls being assessed. Here is how to transform EASM output into
            evidence that auditors can efficiently review.
          </p>

          <h3>What to Export</h3>
          <p>
            Prepare these data sets before your audit engagement:
          </p>
          <ul>
            <li>
              <strong>Asset inventory export:</strong> A complete list of discovered external assets with
              their status, classification, and owner. This demonstrates you maintain an accurate, up-to-date
              inventory of your internet-facing surface (PCI DSS 12.5.1, NIS2 Art 21.2a, SOC 2 CC6.1).
            </li>
            <li>
              <strong>Vulnerability scan history:</strong> Time-stamped records of every scan executed
              during the audit period, including scan type, coverage, and findings count. This shows
              continuous monitoring rather than point-in-time testing (PCI DSS 11.3, SOC 2 CC7.1).
            </li>
            <li>
              <strong>Drift event logs:</strong> A chronological record of detected changes to your
              external surface: new ports, removed services, certificate changes, DNS modifications.
              This demonstrates active change detection (NIS2 Art 21.2b, SOC 2 CC7.1).
            </li>
            <li>
              <strong>Risk score trends:</strong> Charts showing how your overall risk posture changed
              over the audit period. Downward trends demonstrate effective remediation. Upward spikes
              with subsequent resolution demonstrate responsive incident handling.
            </li>
            <li>
              <strong>Remediation timelines:</strong> For each finding, the date discovered, date
              assigned, date resolved, and current status. This demonstrates your vulnerability
              management process has defined SLAs and tracks outcomes (PCI DSS 6.3, NIS2 Art 21.2a).
            </li>
          </ul>

          <h3>How to Present Evidence to Auditors</h3>
          <p>
            Structure your evidence package around the controls being tested, not around the tool that
            generated the data. Auditors evaluate controls, not products.
          </p>
          <ul>
            <li>
              <strong>Organize by control, not by feature.</strong> Create folders or sections named
              after specific requirements (e.g., "PCI DSS 11.3 — External Vulnerability Scanning")
              and place the relevant exports in each.
            </li>
            <li>
              <strong>Include coverage dates.</strong> Every export should clearly state the date range
              it covers. Auditors need to verify that monitoring was continuous across the entire
              audit period with no gaps.
            </li>
            <li>
              <strong>Show the process, not just the data.</strong> Include documentation of your scan
              schedules, escalation procedures, and remediation workflows alongside the raw data.
              Auditors want to see that the data is part of a managed program.
            </li>
            <li>
              <strong>Highlight exceptions and their resolution.</strong> If there were periods where
              monitoring lapsed or findings were not remediated within SLA, document why and what
              compensating controls were in place. Transparency builds auditor confidence.
            </li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Evidence Retention</div>
            <div className="callout-content">
              <p>
                Assessors typically expect the last four quarterly ASV scan reports; PCI DSS separately
                requires twelve months of audit log history, with three months immediately available.
                SOC 2 Type II audits
                typically cover 6-12 months. NIS2 does not specify a retention period but regulatory
                practice suggests maintaining records for the duration of your compliance cycle plus
                one year. Export and archive your EASM data regularly.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Action Steps */}
        <section id="action-steps" className="guide-section">
          <h2>What to Do Now: Prepare Compliance Evidence in 4 Steps</h2>
          <p>
            Whether your next audit is in three months or next quarter, start building your evidence base
            now. The longer your historical monitoring data, the stronger your audit position.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Inventory Your External Surface</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', lineHeight: '1.6' }}>
                Run discovery scans on all domains and IP ranges. Identify every internet-facing asset.
                Classify each by owner, environment, and criticality. This becomes your baseline asset
                register for audit evidence.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Map Controls to Evidence</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', lineHeight: '1.6' }}>
                Review the compliance mapping table above. Identify which controls apply to your
                organization. For each applicable control, determine what EASM data you need to
                collect and how frequently.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Enable Continuous Monitoring</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', lineHeight: '1.6' }}>
                Set up scheduled vulnerability scans, drift detection, and port monitoring on your
                critical assets. Ensure scans run frequently enough to demonstrate continuous coverage
                across your audit period.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Export and Archive Regularly</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '10px', lineHeight: '1.6' }}>
                Schedule monthly exports of scan results, drift events, and risk score trends. Store
                them in your compliance documentation repository. When audit time arrives, you will
                have months of continuous monitoring evidence ready.
              </p>
            </div>
          </div>
        </section>

        {/* Section: DriftAlarm Compliance Support */}
        <section id="driftalarm-compliance" className="guide-section">
          <h2>How DriftAlarm Supports Compliance Workflows</h2>
          <p>
            DriftAlarm is built to help security teams generate the evidence auditors ask for, without
            manual data collection. Here is how specific platform features map to compliance evidence needs.
          </p>

          <h3>Scheduled Scans with Historical Records</h3>
          <p>
            DriftAlarm runs discovery, vulnerability, and deep security scans on configurable schedules.
            Every scan result is time-stamped and stored, building a continuous record across your audit
            period. You can demonstrate to auditors that monitoring ran consistently with no gaps.
          </p>

          <h3>Drift Detection and Alert History</h3>
          <p>
            The drift detection engine monitors your external surface for unauthorized changes: new open
            ports, modified DNS records, certificate expirations, and service configuration shifts. Each
            drift event is logged with a timestamp, the specific change detected, and the asset affected.
            This event log directly supports NIS2 incident prevention requirements and SOC 2 CC7.1
            system monitoring evidence.
          </p>

          <h3>Risk Score Trends and Trend Charts</h3>
          <p>
            The Command Center displays risk score trends over time for each asset and your overall
            surface. These trend charts show whether your security posture is improving, stable, or
            degrading. Auditors use this type of trend data to assess whether your vulnerability
            management program is effective over the audit period.
          </p>

          <h3>Scan Result Exports</h3>
          <p>
            All scan results, asset inventories, and drift events can be exported for inclusion in
            audit evidence packages. Export data includes timestamps, scan configuration, coverage
            scope, and detailed findings, formatted for auditor review.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">DriftAlarm Does Not Provide Compliance Certification</div>
            <div className="callout-content">
              <p>
                DriftAlarm helps generate evidence and supports compliance workflows. It does not
                certify compliance with any framework, and DriftAlarm itself does not hold SOC 2,
                PCI DSS, or NIS2 certification. Compliance determinations are made by your
                qualified assessor based on your complete control environment.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Related Guides */}
        <section id="related-guides" className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Continue building your security program with these related guides:
          </p>

          <div className="playbook-links">
            <Link to="/guides/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/attack-surface-checklist" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Checklist</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/easm-vs-vulnerability-scanning" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">EASM vs Vulnerability Scanning</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/external-attack-surface-management" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">External Attack Surface Management</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
          </div>

          {/* Bottom CTA */}
          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <Link to="/upgrade" className="da-btn da-btn--primary">
              <span>Continuous Compliance Monitoring from $99/mo</span>
              <span>&rarr;</span>
            </Link>
          </div>
        </section>
        <GuideFAQ path="/guides/easm-compliance" />
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default EASMComplianceGuide;
